import React, { useEffect, useRef } from 'react'
import { Animated, Easing, Platform, Pressable, StyleSheet } from 'react-native'
import { SizableText, View, XStack, XStackProps } from 'tamagui'
import { AntDesign } from '@expo/vector-icons'
import { BlurView } from 'expo-blur'
import { t } from 'i18next'
import { Title } from '@constants/Themed'
import { themeSelector } from '@reduxLocal/selectors/themeSelector'
import { responsiveFontSize } from '@utils/responsiveFontSize'
import { responsivePadding } from '@stylesheet'

const wrap = (min: number, max: number, v: number) => {
  const rangeSize = max - min
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min
}

function toSentenceCase(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

type LoginSlideType = 'info' | 'error' | 'warning' | 'success'

type CarouselContent = {
  title: string;
  descriptor: string;
  type?: LoginSlideType;
}[];

const getAccentColor = (type?: LoginSlideType, fallback: string = '#3b82f6') => {
  switch (type) {
    case 'success':
      return '#10b981'
    case 'warning':
      return '#f59e0b'
    case 'error':
      return '#ef4444'
    default:
      return fallback
  }
}

const FADE_OUT_MS = 450
const FADE_IN_MS = 550

export default function HorizontalTextCarousel({ content, height, viewStyle }: { content: CarouselContent, height?: number, viewStyle?: XStackProps }) {
  const [page, setPage] = React.useState(0)
  const fadeAnim = useRef(new Animated.Value(1)).current
  const isFading = useRef(false)
  const theme = themeSelector()
  const isLight = Boolean(theme)

  const contentIndex = wrap(0, content?.length || 0, page)
  const paginate = (direction: number) => {
    if (!content?.length || isFading.current) return
    isFading.current = true

    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: FADE_OUT_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (!finished) {
        isFading.current = false
        return
      }

      setPage((current) => current + direction)

      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: FADE_IN_MS,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }).start(() => {
        isFading.current = false
      })
    })
  }

  useEffect(() => {
    const interval = setInterval(() => {
      paginate(1)
    }, 6000)

    return () => clearInterval(interval)
  }, [page])

  const accentColor = getAccentColor(content?.[contentIndex]?.type)
  const surfaceBg = isLight ? 'rgba(255, 255, 255, 0.82)' : 'rgba(15, 23, 42, 0.78)'
  const contrastingBg = isLight ? 'rgba(241, 245, 249, 0.92)' : 'rgba(30, 41, 59, 0.88)'
  const titleColor = isLight ? '#0f172a' : '#f8fafc'
  const bodyColor = isLight ? '#475569' : '#cbd5e1'
  const borderColor = isLight ? 'rgba(148, 163, 184, 0.35)' : 'rgba(148, 163, 184, 0.25)'
  const buttonFg = isLight ? '#1e293b' : '#f1f5f9'
  const buttonHoverBg = isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(51, 65, 85, 0.95)'
  const blurTint = isLight ? 'light' : 'dark'

  const leftButtonRadii = {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
  }

  const rightButtonRadii = {
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  }

  if (!content) return null

  return (
    <XStack
      overflow="visible"
      position="relative"
      height={height || 300}
      width="100%"
      alignItems="stretch"
      flex={1}
      gap={0}
      {...viewStyle}
    >
      {content.length > 1 && (
        <BlurView
          intensity={Platform.OS === 'web' ? 48 : 36}
          tint={blurTint}
          style={[
            styles.navBlur,
            leftButtonRadii,
            {
              backgroundColor: contrastingBg,
              borderColor,
            },
          ]}
        >
          <Pressable
            onPress={() => paginate(-1)}
            accessibilityRole="button"
            accessibilityLabel={t('Previous') as string}
            style={({ pressed, hovered }: any) => [
              styles.navButton,
              leftButtonRadii,
              {
                backgroundColor: pressed || hovered ? buttonHoverBg : 'transparent',
                transform: [{ scale: pressed ? 0.98 : hovered ? 1.02 : 1 }],
              },
            ]}
          >
            <AntDesign name="arrow-left" size={18} color={buttonFg} />
          </Pressable>
        </BlurView>
      )}

      <Animated.View
        style={[
          styles.slideWrap,
          { opacity: fadeAnim },
        ]}
      >
        <BlurView
          intensity={Platform.OS === 'web' ? 56 : 40}
          tint={blurTint}
          style={[
            styles.contentBlur,
            {
              backgroundColor: surfaceBg,
              borderColor,
              borderLeftColor: accentColor,
            },
          ]}
        >
          <View style={styles.iconWrap}>
            <AntDesign
              name={
                content[contentIndex].type === 'success' ? 'check-circle'
                  : content[contentIndex].type === 'warning' ? 'exclamation-circle'
                    : content[contentIndex].type === 'error' ? 'close-circle'
                      : 'info'
              }
              size={24}
              color={accentColor}
            />
          </View>
          <View style={styles.textWrap}>
            <Title
              selectable={false}
              style={{
                textAlign: 'left',
                fontSize: responsiveFontSize(13),
                fontWeight: '700',
                color: titleColor,
                marginBottom: responsivePadding(4),
              }}
            >
              {toSentenceCase(t(content[contentIndex].title) as string)}
            </Title>
            <SizableText
              textWrap="wrap"
              flexWrap="wrap"
              style={{
                textAlign: 'left',
                fontSize: responsiveFontSize(12),
                color: bodyColor,
                lineHeight: responsiveFontSize(18),
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflow: 'hidden',
                whiteSpace: 'normal',
              }}
              userSelect="none"
            >
              {t(content[contentIndex].descriptor) as string}
            </SizableText>
          </View>
        </BlurView>
      </Animated.View>

      {content.length > 1 && (
        <BlurView
          intensity={Platform.OS === 'web' ? 48 : 36}
          tint={blurTint}
          style={[
            styles.navBlur,
            rightButtonRadii,
            {
              backgroundColor: contrastingBg,
              borderColor,
            },
          ]}
        >
          <Pressable
            onPress={() => paginate(1)}
            accessibilityRole="button"
            accessibilityLabel={t('Next') as string}
            style={({ pressed, hovered }: any) => [
              styles.navButton,
              rightButtonRadii,
              {
                backgroundColor: pressed || hovered ? buttonHoverBg : 'transparent',
                transform: [{ scale: pressed ? 0.98 : hovered ? 1.02 : 1 }],
              },
            ]}
          >
            <AntDesign name="arrow-right" size={18} color={buttonFg} />
          </Pressable>
        </BlurView>
      )}
    </XStack>
  )
}

const styles = StyleSheet.create({
  navBlur: {
    width: 52,
    alignSelf: 'stretch',
    overflow: 'hidden',
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navButton: {
    width: '100%',
    height: '100%',
    minHeight: 110,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: responsivePadding(8),
  },
  slideWrap: {
    flex: 1,
    minWidth: 0,
    height: '100%',
  },
  contentBlur: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: responsivePadding(14),
    paddingHorizontal: responsivePadding(16),
    borderRadius: 20,
    borderWidth: 1,
    borderLeftWidth: 4,
    marginVertical: 0,
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    flexDirection: 'row',
    gap: responsivePadding(12),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
  },
  iconWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: responsivePadding(4),
  },
  textWrap: {
    flex: 1,
    minWidth: 0,
  },
})
